import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { toTitleCase } from 'utility/functions';
import { Chip, useTheme } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import TodayIcon from '@mui/icons-material/Today';
import { getColorMix } from 'utility/styles';
import { bloxValues } from 'themes/constants';

/**
 * CampaignStatusChip — Active / Deactivated chip for emailreach/campaign rows.
 */
export const CampaignStatusChip = memo(({ active }: { active?: boolean }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isActive = active === true;
  const Icon = isActive ? CheckCircleOutlineIcon : DoNotDisturbIcon;
  const statusColor = isActive ? theme.palette.success.main : theme.palette.text.disabled;
  const isDarkMode = theme.palette.mode === 'dark';
  const blackWhite = isDarkMode ? theme.palette.common.black : theme.palette.common.white;

  return (
    <Chip
      icon={<Icon />}
      label={
        isActive
          ? t('blox.email_reach.campaign.table.active_status.active', 'Active')
          : t('blox.email_reach.campaign.table.active_status.deactivated', 'Deactivated')
      }
      size="small"
      variant="filled"
      sx={{
        color: statusColor,
        borderColor: statusColor,
        bgcolor: getColorMix(statusColor, 10, blackWhite, 'srgb'),
        fontSize: bloxValues.smallFontSizeRem,
        fontWeight: bloxValues.fontWeightMedium,
        '.MuiChip-icon': {
          color: 'inherit',
          fontSize: bloxValues.regularFontSizeRem,
        },
      }}
    />
  );
});
CampaignStatusChip.displayName = 'CampaignStatusChip';

/**
 * Icon per campaign frequency.
 * Colors are intentionally NOT varied — the schedule column is informational,
 * not a status indicator. Distinct icons provide the differentiation while
 * keeping the visual weight lower than the Active status column.
 */
const frequencyIcons: Record<string, typeof TodayIcon> = {
  daily: TodayIcon,
  weekly: DateRangeIcon,
  monthly: CalendarMonthIcon,
};

/**
 * CampaignScheduleChip — Outlined chip for emailreach/campaign rows.
 * Uses a single neutral color with the `outlined` variant so it reads
 * as informational rather than competing with the filled Active chip.
 */
export const CampaignScheduleChip = memo(({ frequency }: { frequency?: string }) => {
  const theme = useTheme();
  if (typeof frequency !== 'string' || !frequency) {
    return <>{'\u2014'}</>;
  }

  const Icon = frequencyIcons[frequency.toLowerCase()] ?? EventRepeatIcon;
  const chipColor = theme.palette.text.secondary;

  return (
    <Chip
      icon={<Icon />}
      label={toTitleCase(frequency)}
      size="small"
      variant="outlined"
      sx={{
        color: chipColor,
        borderColor: theme.palette.divider,
        fontSize: bloxValues.smallFontSizeRem,
        fontWeight: bloxValues.fontWeightMedium,
        '.MuiChip-icon': {
          color: 'inherit',
          fontSize: bloxValues.regularFontSizeRem,
        },
      }}
    />
  );
});
CampaignScheduleChip.displayName = 'CampaignScheduleChip';
